// The picture of a flat shape: a named shape with labels on its sides and
// in its corners, or an outline whose sides all meet at right angles. Plain
// React SVG, like practice-graphs.tsx.
//
// Equal sides carry short strokes, parallel sides carry arrow heads and a
// marked right angle carries a small square, the way a school book draws
// them, and only where the question asks for them.

import {
  SHAPE_LABEL_SIZE,
  describeCompositeShape,
  describeShape2d,
  shapePicture,
  type CompositeShapeSpec,
  type Shape2dSpec,
  type ShapePictureLabel,
  type ShapePoint,
} from './practice-shape-data';

const INK = '#2d4a3e';
const SHAPE_FILL = '#eef3ee';

const fixed = (value: number) => value.toFixed(2);
const at = (point: ShapePoint) => `${fixed(point.x)} ${fixed(point.y)}`;

function openPath(points: ShapePoint[]): string {
  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${at(point)}`)
    .join(' ');
}

function Label({
  label,
  where,
}: {
  label: ShapePictureLabel;
  where: 'side' | 'corner' | 'height';
}) {
  return (
    <text
      data-label={where}
      x={fixed(label.x)}
      y={fixed(label.y)}
      dy="0.35em"
      textAnchor="middle"
      fill={INK}
      fontSize={SHAPE_LABEL_SIZE}
      fontWeight="700"
    >
      {label.text}
    </text>
  );
}

export function ShapePictureView({
  shape,
}: {
  shape: Shape2dSpec | CompositeShapeSpec;
}) {
  const picture = shapePicture(shape);
  const { box } = picture;

  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={
          shape.type === 'shape_2d'
            ? describeShape2d(shape)
            : describeCompositeShape(shape)
        }
        viewBox={`${fixed(box.x)} ${fixed(box.y)} ${fixed(box.width)} ${fixed(box.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(box.width * 1.4))}px` }}
      >
        <path
          data-outline="true"
          d={picture.outline}
          fill={SHAPE_FILL}
          stroke={INK}
          strokeWidth="2.2"
          strokeLinejoin="round"
        />
        {picture.dashes.map((dash, index) => (
          <line
            key={index}
            data-mark="height"
            x1={fixed(dash.from.x)}
            y1={fixed(dash.from.y)}
            x2={fixed(dash.to.x)}
            y2={fixed(dash.to.y)}
            stroke={INK}
            strokeWidth="1.6"
            strokeDasharray="5 4"
          />
        ))}
        {picture.squares.map((square, index) => (
          <path
            key={index}
            data-mark="square"
            d={openPath(square)}
            fill="none"
            stroke={INK}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        ))}
        {picture.ticks.map((tick, index) => (
          <line
            key={index}
            data-mark="equal"
            x1={fixed(tick.from.x)}
            y1={fixed(tick.from.y)}
            x2={fixed(tick.to.x)}
            y2={fixed(tick.to.y)}
            stroke={INK}
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        ))}
        {picture.arrows.map((arrow, index) => (
          <path
            key={index}
            data-mark="parallel"
            d={openPath(arrow)}
            fill="none"
            stroke={INK}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {picture.sideLabels.map((label, index) => (
          <Label key={index} label={label} where="side" />
        ))}
        {picture.angleLabels.map((label, index) => (
          <Label key={index} label={label} where="corner" />
        ))}
        {picture.heightLabels.map((label, index) => (
          <Label key={index} label={label} where="height" />
        ))}
      </svg>
    </figure>
  );
}
